
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Archive,
    Database,
    FileText,
    Wand2,
    ArrowRight
} from 'lucide-react';
import { useProject } from '../../context/ProjectContext';

const ProjectHome: React.FC = () => {
    const navigate = useNavigate();
    const { currentProject } = useProject();

    if (!currentProject) {
        return <div className="text-slate-500 text-center py-20">Cargando contexto del proyecto...</div>;
    }

    const projectId = currentProject.value;

    const cards = [
        {
            label: 'Entidades Detectadas',
            count: '3', // Mock
            icon: Archive,
            color: 'text-blue-400',
            bg: 'bg-blue-400/10',
            link: `/projects/${projectId}/entities`,
            action: 'Ver Entidades'
        },
        {
            label: 'Pipeline de Ingesta',
            status: 'IDLE', // Mock
            icon: Database,
            color: 'text-purple-400',
            bg: 'bg-purple-400/10',
            link: `/projects/${projectId}/ingestion`,
            action: 'Ver Pipeline'
        },
        {
            label: 'Oráculos Activos',
            count: '2', // Mock
            icon: FileText,
            color: 'text-green-400',
            bg: 'bg-green-400/10',
            link: `/projects/${projectId}/oracles`,
            action: 'Gestionar Oráculos'
        },
        {
            label: 'Generador',
            status: 'Ready',
            icon: Wand2,
            color: 'text-orange-400',
            bg: 'bg-orange-400/10',
            link: `/projects/${projectId}/generator`,
            action: 'Ir al Generador'
        },
    ];

    return (
        <div className="space-y-8 max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-white mb-2">Workspace: {currentProject.label}</h1>
                    <p className="text-slate-400">Panel de control unificado para ingeniería de pruebas.</p>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={() => navigate(`/projects/${projectId}/settings`)}
                        className="px-4 py-2 border border-slate-700 hover:bg-slate-800 text-slate-300 rounded-lg transition-colors"
                    >
                        Configuración
                    </button>
                    <button
                        onClick={() => navigate(`/projects/${projectId}/generator`)}
                        className="px-4 py-2 bg-primary hover:bg-blue-600 text-white rounded-lg transition-colors flex items-center gap-2"
                    >
                        <Wand2 className="w-4 h-4" />
                        Generar Tests
                    </button>
                </div>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {cards.map((card, i) => (
                    <div key={i} className="bg-surface-dark border border-slate-800 p-6 rounded-xl hover:border-slate-600 transition-all group">
                        <div className="flex justify-between items-start mb-4">
                            <div className={`p-3 rounded-lg ${card.bg} ${card.color}`}>
                                <card.icon className="w-6 h-6" />
                            </div>
                            {card.count && <span className="text-2xl font-bold text-white">{card.count}</span>}
                            {card.status && <span className="text-xs font-mono px-2 py-1 bg-slate-800 rounded text-slate-400 border border-slate-700">{card.status}</span>}
                        </div>

                        <h3 className="text-lg font-semibold text-white mb-2">{card.label}</h3>

                        <button
                            onClick={() => navigate(card.link)}
                            className="text-sm text-slate-400 group-hover:text-white flex items-center gap-1 transition-colors mt-2"
                        >
                            {card.action} <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                        </button>
                    </div>
                ))}
            </div>

            {/* Quick Access / Workflows */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-2 bg-surface-dark border border-slate-800 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-white mb-4">Flujo de Trabajo Sugerido</h3>
                    <div className="flex items-center justify-between text-sm">
                        <div className="flex flex-col items-center gap-2 text-center opacity-50">
                            <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center border border-slate-700">1</div>
                            <span className="text-slate-400">Ingesta Docs</span>
                        </div>
                        <div className="h-[1px] bg-slate-800 flex-1 mx-4"></div>
                        <div className="flex flex-col items-center gap-2 text-center opacity-75">
                            <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center border border-slate-700">2</div>
                            <span className="text-slate-400">Revisión Entidades</span>
                        </div>
                        <div className="h-[1px] bg-slate-800 flex-1 mx-4"></div>
                        <div className="flex flex-col items-center gap-2 text-center">
                            <div className="w-8 h-8 rounded-full bg-primary/20 border border-primary text-primary flex items-center justify-center">3</div>
                            <span className="text-white font-medium">Definir Oráculos</span>
                        </div>
                        <div className="h-[1px] bg-slate-800 flex-1 mx-4"></div>
                        <div className="flex flex-col items-center gap-2 text-center opacity-50">
                            <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center border border-slate-700">4</div>
                            <span className="text-slate-400">Generación</span>
                        </div>
                    </div>
                </div>

                <div className="bg-slate-800/20 border border-dashed border-slate-700 rounded-xl p-6 flex flex-col justify-center items-center text-center">
                    <p className="text-slate-400 mb-4">¿Necesitas ayuda?</p>
                    <a href="https://confluence.banco.cl/qa-smartcases" target="_blank" rel="noreferrer" className="text-primary hover:underline text-sm">
                        Ver Documentación Interna
                    </a>
                </div>
            </div>
        </div>
    );
};

export default ProjectHome;
